import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Checkbox,
  SearchInput,
  Badge,
  ScrollArea,
  toast,
} from '@aaas/shared';
import { Layers, Loader2 } from 'lucide-react';
import { usePoolListQuery } from '../hooks/usePoolQueries';

interface Pool {
  id: string;
  name: string;
}

interface ManageCVPoolsDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with pools to add and pools to remove */
  onSave: (toAdd: string[], toRemove: string[]) => Promise<void>;
  workspaceId: string | undefined;
  idToken: string | undefined;
  existingPools?: Pool[];
}

export function ManageCVPoolsDialog({
  open,
  onClose,
  onSave,
  workspaceId,
  idToken,
  existingPools = [],
}: ManageCVPoolsDialogProps) {
  const { t } = useTranslation('ats');
  const [selectedPoolIds, setSelectedPoolIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: poolsData, isLoading: poolsLoading } = usePoolListQuery({
    workspaceId,
    idToken,
    size: 100,
    search: searchQuery || undefined,
    enabled: open && !!workspaceId && !!idToken,
    refetchInterval: false,
  });

  const existingSet = useMemo(() => new Set(existingPools.map((p) => p.id)), [existingPools]);
  const allPools = useMemo(() => poolsData?.data?.items || [], [poolsData]);

  // Build id→name map from both existing pools and loaded list
  const poolNameMap = useMemo(() => {
    const map = new Map<string, string>();
    existingPools.forEach((p) => map.set(p.id, p.name));
    allPools.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [existingPools, allPools]);

  // Initialize selections from existing pools when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedPoolIds(new Set(existingPools.map((p) => p.id)));
    } else {
      setSelectedPoolIds(new Set());
      setSearchQuery('');
      setError(null);
    }
  }, [open]); // intentionally omit existingPools to avoid re-initializing while open

  const handleToggle = useCallback((poolId: string) => {
    setSelectedPoolIds((prev) => {
      const next = new Set(prev);
      if (next.has(poolId)) {
        next.delete(poolId);
      } else {
        next.add(poolId);
      }
      return next;
    });
  }, []);

  const toAdd = useMemo(
    () => [...selectedPoolIds].filter((id) => !existingSet.has(id)),
    [selectedPoolIds, existingSet]
  );

  const toRemove = useMemo(
    () => [...existingSet].filter((id) => !selectedPoolIds.has(id)),
    [selectedPoolIds, existingSet]
  );

  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  const handleConfirm = useCallback(async () => {
    if (!hasChanges) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onSave(toAdd, toRemove);
      const parts: string[] = [];
      if (toAdd.length > 0) parts.push(t('managePools.toast.added', { count: toAdd.length }));
      if (toRemove.length > 0) parts.push(t('managePools.toast.removed', { count: toRemove.length }));
      toast.success(parts.join(', '));
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('managePools.saveFailed');
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [hasChanges, toAdd, toRemove, onSave, onClose, t]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-500" />
            {t('managePools.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Summary boxes */}
          <div className="flex flex-col gap-3">
            {/* Selected */}
            <div className="space-y-1.5">
              <Label className="text-green-700">
                {t('managePools.selected')}{' '}
                <span className="font-normal text-gray-400">({selectedPoolIds.size})</span>
                {toAdd.length > 0 && (
                  <span className="ml-1 font-medium text-green-600">+{toAdd.length}</span>
                )}
              </Label>
              <div className="min-h-[4rem] border border-green-200 bg-green-50 rounded-lg p-2">
                {selectedPoolIds.size > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {[...selectedPoolIds].map((id) => {
                      const name = poolNameMap.get(id);
                      if (!name) return null;
                      const isNew = !existingSet.has(id);
                      return (
                        <Badge
                          key={id}
                          variant="secondary"
                          className={
                            isNew
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : 'bg-white text-gray-700 border border-gray-200'
                          }
                        >
                          {name}
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">{t('managePools.none')}</p>
                )}
              </div>
            </div>

            {/* To remove */}
            <div className="space-y-1.5">
              <Label className="text-red-600">
                {t('managePools.toRemove')}{' '}
                <span className="font-normal text-gray-400">({toRemove.length})</span>
              </Label>
              <div className="min-h-[4rem] border border-red-200 bg-red-50 rounded-lg p-2">
                {toRemove.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {toRemove.map((id) => {
                      const name = poolNameMap.get(id);
                      if (!name) return null;
                      return (
                        <Badge
                          key={id}
                          variant="secondary"
                          className="bg-red-100 text-red-700 border border-red-200 line-through"
                        >
                          {name}
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">{t('managePools.none')}</p>
                )}
              </div>
            </div>
          </div>

          {/* Pool list */}
          <div className="space-y-2">
            <Label>{t('managePools.pools')}</Label>

            <SearchInput
              placeholder={t('managePools.searchPlaceholder')}
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              width="xl"
              className="w-full"
              showClearButton
            />

            {poolsLoading ? (
              <div className="flex items-center justify-center h-56 border border-gray-200 rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : allPools.length > 0 ? (
              <div className="border border-gray-200 rounded-lg">
                <ScrollArea className="h-56">
                  {allPools.map((pool) => {
                    const isChecked = selectedPoolIds.has(pool.id);
                    const isExisting = existingSet.has(pool.id);
                    return (
                      <label
                        key={pool.id}
                        className="flex items-center gap-3 p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                      >
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => handleToggle(pool.id)}
                        />
                        <span className="text-sm text-gray-900 flex-1">{pool.name}</span>
                        {isExisting && !isChecked && (
                          <span className="text-xs text-red-400">{t('managePools.willRemove')}</span>
                        )}
                        {!isExisting && isChecked && (
                          <span className="text-xs text-green-500">{t('managePools.willAdd')}</span>
                        )}
                      </label>
                    );
                  })}
                </ScrollArea>
              </div>
            ) : searchQuery ? (
              <div className="flex items-center justify-center h-56 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-500">
                  {t('managePools.noPoolsMatching', { query: searchQuery })}
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center h-56 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-500">{t('managePools.noPoolsAvailable')}</p>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('managePools.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!hasChanges || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('managePools.saving')}
              </>
            ) : (
              t('managePools.saveChanges')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
